/**
 * Saidas.js — a tela "Saídas": cada saída decidida pela liberação, liberada ou bloqueada, e por quê (Onda 183)
 * CI Sistemas de Informática Ltda. — AcessoiD
 *
 * Pedido do Iatagã em 19/10/2026, com a controladora de saída na bancada: "um painel para sabermos se a saída
 * do veículo foi liberada ou não, e no caso de bloqueio, o motivo". A tela lê `gate_decisions` pela rota
 * `/api/exit-release` e recebe cada decisão nova pelo socket (`exit_decision`), sem recarregar.
 *
 *   - os cartões do dia: liberadas, bloqueadas, sem confirmação da controladora, liberações manuais;
 *   - a situação da liberação: desligada, observação (nenhum relé) ou automática (controladoras e se estão vivas);
 *   - a lista, com a decisão, o motivo em português, o dono da placa, a controladora e o evento ao clube;
 *   - o clique abre a foto e o recorte da leitura, e, no bloqueio, a última autorização de saída da placa;
 *   - "Liberar agora" (operador para cima, câmera com controladora): pulsa o relé com justificativa, auditado;
 *   - "Tela grande": a guarita deixa aberta — só as últimas decisões, em letra grande.
 *
 * Onda 197: no condomínio a tela é a Movimentação (Movimentacao.js) — cada entrada e saída registrada, com a decisão
 * pendurada na saída. No clube, abre nas decisões, como sempre; "Mostrar" leva às entradas, ou às duas.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiLogOut, FiRefreshCw, FiMaximize2, FiMinimize2, FiUnlock, FiX, FiFilter } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import PlacaVeicular from '../components/PlacaVeicular';
import Pagination from '../components/Pagination';
import { hojeLocal } from '../utils/dataLocal';
import Movimentacao from './Movimentacao';
import '../styles/Saidas.css';

const PAGINA_PADRAO = 50;
const TELA_GRANDE_LINHAS = 12;
// Onda 185: "Liberar agora" vale 5 minutos e só enquanto a placa não tiver decisão mais nova na mesma câmera.
const PRAZO_LIBERACAO_MS = 5 * 60 * 1000;
function prazoDe(d) { return d.open_until ? Date.parse(d.open_until) : Date.parse(d.created_at) + PRAZO_LIBERACAO_MS; }

function fileUrl(relPath, token) { return relPath ? `/api/ocr-quality/file/${relPath}?token=${encodeURIComponent(token || '')}` : null; }
function hora(v) { const d = new Date(v); return Number.isNaN(d.getTime()) ? '—' : d.toLocaleTimeString('pt-BR'); }
function dataHora(v) { const d = new Date(v); return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('pt-BR'); }
function dia(v) { const d = new Date(v); return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('pt-BR'); }

const SITUACAO_CONTROLADORA = {
  confirmada: { classe: 'ok', texto: 'confirmada', dica: 'A controladora confirmou o pulso do relé' },
  sem_confirmacao: { classe: 'bad', texto: 'sem confirmação', dica: 'O comando saiu, mas a controladora não confirmou: a cancela pode não ter aberto' },
  observacao: { classe: 'muted', texto: 'observação', dica: 'Abertura automática desligada: nenhum relé acionado' },
  sem_controladora: { classe: 'muted', texto: 'sem controladora', dica: 'A câmera não tem controladora de saída vinculada' },
};
const EVENTO = {
  delivered: { classe: 'ok', texto: 'entregue' }, pending: { classe: 'warn', texto: 'na fila' },
  failed: { classe: 'bad', texto: 'falhou' }, expired: { classe: 'bad', texto: 'expirado' },
};

export function Dono({ owner, classification }) {
  if (!owner) {
    if (classification === 'visitor') return <span className="saidas-dono saidas-dono--vazio">visitante (sem cadastro)</span>;
    return <span className="saidas-dono saidas-dono--vazio">—</span>;
  }
  const local = [owner.bloco, owner.unidade].filter(Boolean).join(' / ');
  return (
    <span className="saidas-dono">
      <span className="saidas-dono-nome">{owner.nome || '—'}</span>
      <span className="saidas-dono-sub">
        {local && <span>{local}</span>}
        <span className="saidas-badge muted">{owner.tipo}</span>
        {owner.ativo === false && <span className="saidas-badge warn">inativo</span>}
        {owner.tipo === 'visitante' && owner.autorizacao === 'vencida' && <span className="saidas-badge warn">autorização vencida</span>}
        {owner.tipo === 'visitante' && owner.autorizacao === 'vigente' && <span className="saidas-badge ok">autorizado</span>}
      </span>
    </span>
  );
}

function Controladora({ d }) {
  const s = SITUACAO_CONTROLADORA[d.controller_status];
  if (!s) return <span className="saidas-badge muted">—</span>;
  return (
    <span className="saidas-ctrl">
      <span className={`saidas-badge ${s.classe}`} title={s.dica}>{s.texto}</span>
      {d.device && d.controller_status !== 'observacao' && <span className="saidas-ctrl-rele">{d.device} · relé {d.relay}</span>}
    </span>
  );
}

function Evento({ d }) {
  if (!d.event_id) return <span className="saidas-badge muted" title="Nenhum evento de saída enviado ao sistema de gestão">—</span>;
  const e = EVENTO[d.event_status] || { classe: 'muted', texto: d.event_status || 'enviado' };
  return <span className={`saidas-badge ${e.classe}`} title={`Evento ${d.event_id}`}>{e.texto}</span>;
}

/** A última autorização de saída da placa bloqueada: o que faltou, para a guarita explicar ao motorista. */
function UltimaAutorizacao({ a }) {
  if (!a) return <p className="saidas-detalhe-aviso">Nenhum pagamento nem isenção para esta placa nos últimos 2 dias.</p>;
  const tipo = a.kind === 'exempt' ? 'Isenção' : 'Pagamento';
  const situacao = { pending: 'válida', used: 'já usada', expired: 'vencida', cancelled: 'cancelada' }[a.status] || a.status;
  return (
    <p className="saidas-detalhe-aviso">
      Última autorização: <strong>{tipo}</strong> ({a.external_payment_id}) às {dataHora(a.paid_at)}, válida até {dataHora(a.valid_until)} — <strong>{situacao}</strong>
      {a.used_at ? ` em ${dataHora(a.used_at)}` : ''}.
    </p>
  );
}

function ModalDetalhe({ d, token, onFechar, podeLiberar, onLiberar, encerrada }) {
  const foto = fileUrl(d.image_path, token);
  const recorte = fileUrl(d.plate_crop_path, token);
  const liberada = d.decision === 'abriu';
  return (
    <div className="saidas-modal-overlay" onClick={onFechar} role="presentation">
      <div className="saidas-modal" onClick={e => e.stopPropagation()} role="dialog" aria-label={`Saída de ${d.plate}`}>
        <div className="saidas-modal-cabecalho">
          <div>
            <PlacaVeicular placa={d.plate} tamanho="g" />
            <div className="saidas-modal-titulo">
              <span className={`saidas-decisao ${liberada ? 'ok' : 'bad'}`}>{liberada ? 'Liberada' : 'Bloqueada'}</span>
              <span className="saidas-modal-motivo">{d.reason_label}</span>
            </div>
            <div className="saidas-modal-sub">{d.camera || `câmera ${d.camera_id}`} · {dataHora(d.created_at)} · modo {d.mode_label}</div>
          </div>
          <button type="button" className="saidas-btn-fechar" onClick={onFechar} aria-label="Fechar"><FiX /></button>
        </div>
        <div className="saidas-modal-corpo">
          <div className="saidas-modal-fotos">
            {foto ? <img className="saidas-foto" src={foto} alt={`Foto da leitura de ${d.plate}`} /> : <div className="saidas-foto saidas-foto--vazia">sem foto da leitura</div>}
            {recorte && <img className="saidas-recorte" src={recorte} alt={`Recorte da placa ${d.plate}`} />}
          </div>
          <dl className="saidas-modal-dados">
            <dt>Quem</dt><dd><Dono owner={d.owner} classification={d.classification} /></dd>
            <dt>Controladora</dt><dd><Controladora d={d} /></dd>
            <dt>Evento ao sistema de gestão</dt><dd><Evento d={d} /></dd>
            {d.detail && <><dt>Detalhe</dt><dd>{d.detail}</dd></>}
            {d.mode === 'manual' && <><dt>Liberada por</dt><dd>{d.operator_name || d.operator_id || '—'}: {d.justification}</dd></>}
          </dl>
          {!liberada && <UltimaAutorizacao a={d.last_authorization} />}
          {encerrada && <p className="saidas-detalhe-aviso">{encerrada}</p>}
          {podeLiberar && (
            <div className="saidas-modal-acoes">
              <button type="button" className="saidas-btn saidas-btn--liberar" onClick={() => onLiberar(d)}><FiUnlock /> Liberar agora</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ModalLiberar({ d, onFechar, onConfirmar, enviando }) {
  const [justificativa, setJustificativa] = useState('');
  const ok = justificativa.trim().length >= 5;
  return (
    <div className="saidas-modal-overlay" onClick={onFechar} role="presentation">
      <div className="saidas-modal saidas-modal--pequeno" onClick={e => e.stopPropagation()} role="dialog" aria-label="Liberar agora">
        <div className="saidas-modal-cabecalho">
          <div>
            <h3>Liberar a saída de {d.plate}</h3>
            <div className="saidas-modal-sub">{d.camera || `câmera ${d.camera_id}`} · pulsa o relé {d.relay ? `${d.relay} de ${d.device}` : 'da controladora da câmera'}</div>
          </div>
          <button type="button" className="saidas-btn-fechar" onClick={onFechar} aria-label="Fechar"><FiX /></button>
        </div>
        <div className="saidas-modal-corpo">
          <label className="saidas-campo">
            <span>Justificativa (fica na auditoria, com o seu nome)</span>
            <textarea rows={3} value={justificativa} onChange={e => setJustificativa(e.target.value)} placeholder="Ex.: pagou no caixa, o sistema não registrou" autoFocus />
          </label>
          <div className="saidas-modal-acoes">
            <button type="button" className="saidas-btn" onClick={onFechar} disabled={enviando}>Cancelar</button>
            <button type="button" className="saidas-btn saidas-btn--liberar" disabled={!ok || enviando} onClick={() => onConfirmar(d, justificativa.trim())}>
              <FiUnlock /> {enviando ? 'Enviando…' : 'Liberar agora'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Onda 188: as regras em vigor dependem da vertical configurada.
function regrasDe(vertical) {
  return String(vertical || '').startsWith('condominio')
    ? 'Libera: veículo de morador ativo e visitante com autorização vigente. Bloqueia: placa desconhecida, morador inativo e visitante sem autorização.'
    : 'Libera: saída na tolerância (se ligada), visitante com autorização vigente, pagamento ou isenção registrados no prazo. Bloqueia: sem nenhuma delas.';
}

/**
 * Onda 197: a tela escolhe a visão. Condomínio: a Movimentação, sempre (entradas e saídas). Clube: as decisões de
 * saída, e a Movimentação quando "Mostrar" pede entradas ou as duas.
 */
export default function Saidas() {
  const { vertical } = useAuth();
  const ehCondominio = String(vertical || '').startsWith('condominio');
  const [sentido, setSentido] = useState(ehCondominio ? 'todos' : 'decisoes');
  const [liberando, setLiberando] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [recarregar, setRecarregar] = useState(0);

  if (sentido === 'decisoes' && !ehCondominio) return <SaidasDecisoes onSentido={setSentido} />;

  async function liberar(d, justificativa) {
    setEnviando(true);
    try {
      const r = await api.post(`/api/exit-release/decisions/${d.id}/open`, { justification: justificativa });
      const nova = r.data.decision;
      toast.success(nova.published ? `Saída de ${d.plate} liberada: a controladora confirmou` : `Comando enviado para ${d.plate}, mas a controladora NÃO confirmou`);
      setLiberando(null);
      setRecarregar(n => n + 1);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Não foi possível liberar a saída');
    } finally { setEnviando(false); }
  }

  return (
    <>
      <Movimentacao ehCondominio={ehCondominio} sentido={sentido} onSentido={setSentido} onLiberar={setLiberando}
        recarregar={recarregar} regrasDaVertical={regrasDe(vertical)} />
      {liberando && <ModalLiberar d={liberando} onFechar={() => setLiberando(null)} onConfirmar={liberar} enviando={enviando} />}
    </>
  );
}

function SaidasDecisoes({ onSentido }) {
  const { token, socket, can, vertical } = useAuth();
  const podeLiberar = typeof can === 'function' ? !!can('exit_release_open') : false;
  const regrasDaVertical = regrasDe(vertical);

  const [filtros, setFiltros] = useState({ date_from: hojeLocal(), date_to: hojeLocal(), camera_id: '', decision: '', reason: '', plate: '' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGINA_PADRAO);
  const [itens, setItens] = useState([]);
  const [total, setTotal] = useState(0);
  const [motivos, setMotivos] = useState({});
  const [resumo, setResumo] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [temEntrada, setTemEntrada] = useState(false);   // Onda 197
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState('');
  const [detalhe, setDetalhe] = useState(null);
  const [liberando, setLiberando] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [telaGrande, setTelaGrande] = useState(() => { try { return localStorage.getItem('saidas.telaGrande') === '1'; } catch (_) { return false; } });

  const hoje = hojeLocal();
  const vendoHoje = filtros.date_from === hoje && filtros.date_to === hoje;
  const filtrosRef = useRef(filtros); filtrosRef.current = filtros;
  const pageRef = useRef(page); pageRef.current = page;

  const carregarLista = useCallback(async () => {
    setCarregando(true); setErro('');
    try {
      const p = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
      for (const [k, v] of Object.entries(filtros)) if (v) p.set(k, v);
      const r = await api.get(`/api/exit-release/decisions?${p.toString()}`);
      setItens(r.data.items || []); setTotal(r.data.total || 0); setMotivos(r.data.reasons || {});
    } catch (e) {
      setErro(e.response?.data?.error || 'Não foi possível carregar as saídas');
    } finally { setCarregando(false); }
  }, [filtros, page, pageSize]);

  const carregarResumo = useCallback(async () => {
    try { const r = await api.get(`/api/exit-release/summary?date=${filtros.date_to}`); setResumo(r.data); } catch (_) { setResumo(null); }
  }, [filtros.date_to]);

  useEffect(() => { carregarLista(); }, [carregarLista]);
  useEffect(() => { carregarResumo(); }, [carregarResumo]);
  useEffect(() => {
    api.get('/api/cameras').then(r => {
      const todas = Array.isArray(r.data) ? r.data : [];
      setCameras(todas.filter(c => c.camera_type === 'saida'));
      setTemEntrada(todas.some(c => c.camera_type === 'entrada' && c.status !== 'inativa'));
    }).catch(() => setCameras([]));
  }, []);
  useEffect(() => { try { localStorage.setItem('saidas.telaGrande', telaGrande ? '1' : '0'); } catch (_) { /* sem armazenamento: só não lembra */ } }, [telaGrande]);

  // A decisão nova entra no topo quando a tela mostra o dia de hoje sem filtro que a exclua; o resumo recarrega.
  useEffect(() => {
    if (!socket || typeof socket.on !== 'function') return undefined;
    const chegou = (d) => {
      const f = filtrosRef.current;
      const h = hojeLocal();
      const cabe = f.date_from <= h && f.date_to >= h && pageRef.current === 1
        && (!f.camera_id || String(d.camera_id) === String(f.camera_id))
        && (!f.decision || d.decision === f.decision)
        && (!f.reason || d.reason === f.reason)
        && (!f.plate || String(d.plate).includes(f.plate.toUpperCase()));
      if (cabe) {
        const bruta = { ...d, reason_label: d.reason_label || motivosRef.current[d.reason] || d.reason, mode_label: d.mode_label || d.mode, controller_status: d.controller_status || situacao(d) };
        // Onda 185: a decisão nova supera as anteriores da mesma placa nesta câmera (o botão delas some).
        setItens(prev => (prev.some(x => x.id === d.id) ? prev
          : [bruta, ...prev.map(x => (x.plate === d.plate && String(x.camera_id) === String(d.camera_id) && x.id < d.id ? { ...x, superseded: true } : x))].slice(0, pageSizeRef.current)));
        setTotal(t => t + 1);
        // 183e: o socket traz só a decisão; a foto, o recorte, o dono e o evento vêm da lista — busca a linha
        // completa e troca na tela (RBO5G70, 20/10/2026: "sem foto" numa saída que a plataforma leu e liberou).
        api.get(`/api/exit-release/decisions?plate=${encodeURIComponent(d.plate)}&page_size=5`)
          .then(r => {
            const completa = (r.data.items || []).find(x => x.id === d.id);
            if (completa) setItens(prev => prev.map(x => (x.id === d.id ? completa : x)));
          })
          .catch(() => { /* fica a linha bruta; o recarregar traz o resto */ });
      }
      carregarResumo();
    };
    socket.on('exit_decision', chegou);
    return () => { if (typeof socket.off === 'function') socket.off('exit_decision', chegou); };
  }, [socket, carregarResumo]);
  const motivosRef = useRef(motivos); motivosRef.current = motivos;
  const pageSizeRef = useRef(pageSize); pageSizeRef.current = pageSize;

  function situacao(d) {
    if (d.mode === 'observacao') return 'observacao';
    if (d.decision !== 'abriu') return null;
    if (!d.device) return 'sem_controladora';
    return d.published ? 'confirmada' : 'sem_confirmacao';
  }

  function mudarFiltro(k, v) { setFiltros(f => ({ ...f, [k]: v })); setPage(1); }
  function limparFiltros() { setFiltros({ date_from: hoje, date_to: hoje, camera_id: '', decision: '', reason: '', plate: '' }); setPage(1); }

  async function liberar(d, justificativa) {
    setEnviando(true);
    try {
      const r = await api.post(`/api/exit-release/decisions/${d.id}/open`, { justification: justificativa });
      const nova = r.data.decision;
      toast.success(nova.published ? `Saída de ${d.plate} liberada: a controladora confirmou` : `Comando enviado para ${d.plate}, mas a controladora NÃO confirmou`);
      setLiberando(null); setDetalhe(null);
      if (vendoHoje && page === 1) setItens(prev => (prev.some(x => x.id === nova.id) ? prev : [nova, ...prev]));
      carregarResumo();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Não foi possível liberar a saída');
    } finally { setEnviando(false); }
  }

  const lista = useMemo(() => (telaGrande ? itens.slice(0, TELA_GRANDE_LINHAS) : itens), [itens, telaGrande]);
  const totalPaginas = Math.max(1, Math.ceil(total / pageSize));
  // Onda 185: o relógio da tela avança a cada 30 s para o botão vencer sozinho, sem recarregar.
  const [agora, setAgora] = useState(() => Date.now());
  useEffect(() => { const t = setInterval(() => setAgora(Date.now()), 30000); return () => clearInterval(t); }, []);
  const liberavel = (d) => (d.decision === 'recusou' || d.controller_status === 'sem_confirmacao') && !!d.device;
  const encerrada = (d) => liberavel(d) && (d.superseded || agora >= prazoDe(d));
  const podeLiberarLinha = (d) => podeLiberar && liberavel(d) && !encerrada(d);
  const motivoEncerrada = (d) => (d.superseded ? 'Esta placa já tem decisão mais nova nesta câmera' : `Bloqueio de ${hora(d.created_at)}: a liberação pela tela vale 5 min. Libere pela botoeira.`);

  const situacaoDaLiberacao = !resumo ? null
    : !resumo.liberacao_ligada ? { classe: 'off', texto: 'A liberação de saída está desligada: nenhuma decisão nova é tomada. A lista mostra o histórico.' }
    : !resumo.abertura_automatica ? { classe: 'obs', texto: 'Modo observação: a plataforma decide e registra, mas nenhum relé é acionado — a cancela e o portão abrem pela botoeira.' }
    : { classe: 'auto', texto: 'Abertura automática ligada: cada saída liberada pulsa o relé da controladora da câmera.' };

  return (
    <div className={`saidas-page${telaGrande ? ' saidas-page--grande' : ''}`}>
      <div className="saidas-cabecalho">
        <div>
          <h1 className="saidas-titulo"><FiLogOut /> Saídas</h1>
          {!telaGrande && <p className="saidas-subtitulo">Cada saída decidida pela liberação: liberada ou bloqueada, e por quê. As decisões novas chegam sozinhas.</p>}
        </div>
        <div className="saidas-acoes">
          <button type="button" className="saidas-btn" onClick={() => { carregarLista(); carregarResumo(); }} disabled={carregando} title="Recarregar"><FiRefreshCw /> {telaGrande ? '' : 'Atualizar'}</button>
          <button type="button" className="saidas-btn" onClick={() => setTelaGrande(v => !v)} title={telaGrande ? 'Voltar à tela normal' : 'Tela grande para a guarita'}>
            {telaGrande ? <FiMinimize2 /> : <FiMaximize2 />} {telaGrande ? 'Tela normal' : 'Tela grande'}
          </button>
        </div>
      </div>

      {situacaoDaLiberacao && (
        <div className={`saidas-situacao saidas-situacao--${situacaoDaLiberacao.classe}`}>
          <span>{situacaoDaLiberacao.texto} <span className="saidas-regras">{regrasDaVertical}</span></span>
          {resumo && resumo.abertura_automatica && resumo.controladoras && resumo.controladoras.length > 0 && (
            <span className="saidas-controladoras">
              {resumo.controladoras.map(c => (
                <span key={`${c.codigo}-${c.camera_id}`} className={`saidas-badge ${c.online ? 'ok' : 'bad'}`} title={c.visto_em ? `Vista pela última vez em ${dataHora(c.visto_em)}` : 'Nunca vista'}>
                  {c.codigo} relé {c.rele} · {c.camera}: {c.online ? 'viva' : 'fora'}
                </span>
              ))}
            </span>
          )}
        </div>
      )}

      {resumo && (
        <div className="saidas-cartoes">
          <div className="saidas-cartao ok"><span className="saidas-cartao-n">{resumo.liberadas}</span><span className="saidas-cartao-l">liberadas {vendoHoje ? 'hoje' : `em ${dia(resumo.date)}`}</span></div>
          <div className="saidas-cartao bad"><span className="saidas-cartao-n">{resumo.bloqueadas}</span><span className="saidas-cartao-l">bloqueadas</span></div>
          <div className={`saidas-cartao ${resumo.sem_confirmacao ? 'warn' : ''}`}><span className="saidas-cartao-n">{resumo.sem_confirmacao}</span><span className="saidas-cartao-l">sem confirmação da controladora</span></div>
          <div className="saidas-cartao"><span className="saidas-cartao-n">{resumo.manuais}</span><span className="saidas-cartao-l">liberações pela tela</span></div>
          {!telaGrande && resumo.por_motivo && resumo.por_motivo.length > 0 && (
            <div className="saidas-cartao saidas-cartao--motivos">
              {resumo.por_motivo.map(m => <span key={m.reason} className="saidas-badge muted">{m.label}: <strong>{m.n}</strong></span>)}
            </div>
          )}
        </div>
      )}

      {!telaGrande && (
        <div className="saidas-filtros">
          <label><span>De</span><input type="date" value={filtros.date_from} onChange={e => mudarFiltro('date_from', e.target.value)} /></label>
          <label><span>Até</span><input type="date" value={filtros.date_to} onChange={e => mudarFiltro('date_to', e.target.value)} /></label>
          {temEntrada && onSentido && (
            <label><span>Mostrar</span>
              <select value="decisoes" onChange={e => onSentido(e.target.value)} aria-label="Mostrar">
                <option value="decisoes">Saídas (decisões)</option>
                <option value="todos">Entradas e saídas</option>
                <option value="entrada">Entradas</option>
              </select>
            </label>
          )}
          <label><span>Câmera</span>
            <select value={filtros.camera_id} onChange={e => mudarFiltro('camera_id', e.target.value)}>
              <option value="">todas</option>
              {cameras.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label><span>Decisão</span>
            <select value={filtros.decision} onChange={e => mudarFiltro('decision', e.target.value)}>
              <option value="">todas</option><option value="abriu">liberadas</option><option value="recusou">bloqueadas</option>
            </select>
          </label>
          <label><span>Motivo</span>
            <select value={filtros.reason} onChange={e => mudarFiltro('reason', e.target.value)}>
              <option value="">todos</option>
              {Object.entries(motivos).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <label><span>Placa</span><input type="text" value={filtros.plate} placeholder="ABC1D23" maxLength={10} onChange={e => mudarFiltro('plate', e.target.value.toUpperCase())} /></label>
          <button type="button" className="saidas-btn" onClick={limparFiltros} title="Voltar para hoje, sem filtros"><FiFilter /> Hoje</button>
        </div>
      )}

      {erro && <div className="saidas-erro">{erro}</div>}

      <div className="saidas-tabela-caixa">
        <table className="saidas-tabela">
          <thead>
            <tr>
              <th>Hora</th><th>Placa</th><th>Câmera</th><th>Decisão</th><th>Motivo</th>
              {!telaGrande && <th>Quem</th>}
              <th>Controladora</th>
              {!telaGrande && <th>Evento</th>}
              {podeLiberar && <th aria-label="Ações" />}
            </tr>
          </thead>
          <tbody>
            {lista.length === 0 && (
              <tr><td colSpan={9} className="saidas-vazio">{carregando ? 'Carregando…' : 'Nenhuma saída decidida no período.'}</td></tr>
            )}
            {lista.map(d => {
              const liberada = d.decision === 'abriu';
              return (
                <tr key={d.id} className={`saidas-linha ${liberada ? 'ok' : 'bad'}${d.mode === 'manual' ? ' manual' : ''}`} onClick={() => setDetalhe(d)}>
                  <td className="saidas-hora" title={dataHora(d.created_at)}>{vendoHoje || telaGrande ? hora(d.created_at) : dataHora(d.created_at)}</td>
                  <td><PlacaVeicular placa={d.plate} tamanho={telaGrande ? 'g' : 'p'} /></td>
                  <td>{d.camera || `câmera ${d.camera_id}`}</td>
                  <td><span className={`saidas-decisao ${liberada ? 'ok' : 'bad'}`}>{liberada ? 'Liberada' : 'Bloqueada'}</span></td>
                  <td className="saidas-motivo">{d.reason_label}{d.mode === 'manual' && d.operator_name ? <span className="saidas-motivo-sub">por {d.operator_name}</span> : null}</td>
                  {!telaGrande && <td><Dono owner={d.owner} classification={d.classification} /></td>}
                  <td><Controladora d={d} /></td>
                  {!telaGrande && <td><Evento d={d} /></td>}
                  {podeLiberar && (
                    <td className="saidas-acao" onClick={e => e.stopPropagation()}>
                      {podeLiberarLinha(d) && (
                        <button type="button" className="saidas-btn saidas-btn--liberar saidas-btn--mini" onClick={() => setLiberando(d)} title="Pulsar o relé da controladora desta câmera, com justificativa"><FiUnlock /> Liberar</button>
                      )}
                      {encerrada(d) && <span className="saidas-badge muted" title={motivoEncerrada(d)}>liberação encerrada</span>}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!telaGrande && (
        <Pagination currentPage={page} totalPages={totalPaginas} pageSize={pageSize} totalRecords={total}
          onPageChange={setPage} onPageSizeChange={(n) => { setPageSize(n); setPage(1); }} itemLabel="decisão" />
      )}

      {detalhe && <ModalDetalhe d={detalhe} token={token} onFechar={() => setDetalhe(null)} podeLiberar={podeLiberarLinha(detalhe)} encerrada={encerrada(detalhe) ? motivoEncerrada(detalhe) : null} onLiberar={(d) => { setDetalhe(null); setLiberando(d); }} />}
      {liberando && <ModalLiberar d={liberando} onFechar={() => setLiberando(null)} onConfirmar={liberar} enviando={enviando} />}
    </div>
  );
}
